/**
 * @file Reusable hook for OAuth connection flow.
 * Encapsulates OAuth window handling, connection creation, post-connection config state,
 * and config dialog rendering helpers. Used by both Settings ConnectionsTab and Onboarding.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '@/store/appStore';
import {
  useCreateConnection,
  useCreateDataset,
  useStartConnectCardSync,
} from '@/hooks/useConnections';
import { useCloseWindow } from '@/hooks/useCloseWindow';
import { openOAuthWindow } from '@/hooks/useOauth';
import { toast } from '@strivelabs/ui-core';
import { logger } from '@/lib/logger';
import { ApiError } from '@/services/DataService';
import type { App } from '@/services/DataService';
import type { OauthPayload, PostConnectionConfig } from '@/types/connections';
import {
  ANALYTICS_EVENTS,
  useTrack,
  type IntegrationAuthType,
} from '@/lib/analytics';

const APPS_REQUIRING_CONFIG = ['google_ads', 'google_analytics', 'google_search_console', 'google_ads_dataset', 'slack'];

/** App auth types are open-ended strings; anything unrecognised is an OAuth popup. */
const toAuthType = (authType: string | undefined): IntegrationAuthType =>
  authType === 'basic' || authType === 'api_key' ? authType : 'oauth';

/**
 * Apps whose dataset is created automatically as soon as the connection is made
 * (right before the config modal opens), instead of via a manual sync toggle.
 */
const APPS_WITH_AUTO_DATASET = ['google_analytics'];

/**
 * Maps a user-facing app to the hidden "dataset" sub-app that provisions its
 * warehouse (Fivetran) sync. The sub-app has its own OAuth flow/project — the
 * one-card design surfaces a "Sync historical data" action on the parent card
 * that runs the sub-app's existing (untouched) connect flow.
 *
 * NOTE (unverified): the OAuth round-trip for the sub-app needs a browser +
 * a real Google consent to validate end to end.
 */
const DATASET_SYNC_SUBAPPS: Record<
  string,
  { short_name: string; display_name: string; connect_card?: boolean }
> = {
  google_ads: { short_name: 'google_ads_dataset', display_name: 'Google Ads (Dataset)' },
  // Authorized on a Fivetran-hosted page rather than through our own OAuth:
  // LinkedIn only grants ads-read scopes to Marketing Developer Platform
  // partners, and its refresh token expires yearly even for approved apps.
  linkedin: {
    short_name: 'linkedin_ads',
    display_name: 'LinkedIn Ads (Dataset)',
    connect_card: true,
  },
  // Keyed on itself, unlike the entries above: Meta has no OAuth app yet, so the
  // warehouse sync IS the whole integration and `meta_ads` is its own card. When
  // the Meta OAuth app lands this re-keys to `meta` and `meta_ads` goes PRIVATE,
  // matching LinkedIn exactly.
  meta_ads: {
    short_name: 'meta_ads',
    display_name: 'Meta Ads',
    connect_card: true,
  },
};

interface UseConnectionFlowOptions {
  apps: App[];
  onConnectionChange?: () => void;
}

export function useConnectionFlow({ apps, onConnectionChange }: UseConnectionFlowOptions) {
  const [connectingApp, setConnectingApp] = useState<App | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const connectingRef = useRef(false);
  const oauthPopupRef = useRef<Window | null>(null);
  const [postConnectionConfig, setPostConnectionConfig] = useState<PostConnectionConfig | null>(null);

  const createConnectionMutation = useCreateConnection();
  const createDatasetMutation = useCreateDataset();
  const startConnectCardSyncMutation = useStartConnectCardSync();
  const trackEvent = useTrack();

  const accessToken = useAppStore((state) => state.authDetails.accessToken);
  const refreshToken = useAppStore((state) => state.authDetails.refreshToken);
  const userId = useAppStore((state) => state.authDetails.userId);
  const userName = useAppStore((state) => state.authDetails.userName);
  const userEmail = useAppStore((state) => state.authDetails.userEmail);
  const setAuthDetails = useAppStore((state) => state.setAuthDetails);

  useCloseWindow();

  const [basicAuthApp, setBasicAuthApp] = useState<App | null>(null);
  const [apiKeyApp, setApiKeyApp] = useState<App | null>(null);

  // Kick off a Fivetran-hosted authorization. The server creates the dataset and
  // a paused connection, then returns a one-day URL the user authorizes on.
  // Fivetran's webhook finishes setup, so the popup carries no token back.
  const startConnectCard = useCallback(
    (appName: string, displayName: string) => {
      setIsConnecting(true);
      startConnectCardSyncMutation
        .mutateAsync({ appName, displayName })
        .then((result) => {
          window.open(result.connect_card_uri, '', 'width=800,height=600');
        })
        .catch((error) => {
          logger(error as Error, { context: 'startConnectCard' });
          toast({
            title: 'Error',
            description:
              error instanceof ApiError ? error.message : 'Could not start data sync',
            variant: 'destructive',
          });
        })
        .finally(() => {
          setIsConnecting(false);
        });
    },
    [startConnectCardSyncMutation],
  );

  const handleConnectApp = useCallback(
    (app: App) => {
      if (isConnecting) return;
      const authType = app.auth?.[0]?.type;

      // An app declaring no `auth` has nothing to authorize, so every "Connect"
      // affordance on its card would otherwise open /auth/oauth/<app>/default and
      // fail with "App auth details not found". Meta Ads is warehouse-only until
      // its OAuth app exists; route those buttons to the warehouse sync instead.
      // Guarding here rather than in the view covers all five connect buttons at
      // once, and goes inert on its own once the app declares real auth.
      const syncOnly = DATASET_SYNC_SUBAPPS[app.short_name];
      if (!app.auth?.length && syncOnly?.connect_card) {
        startConnectCard(syncOnly.short_name, syncOnly.display_name);
        return;
      }

      trackEvent(ANALYTICS_EVENTS.INTEGRATION_CONNECT_STARTED, {
        provider: app.short_name,
        auth_type: toAuthType(authType),
      });
      if (authType === 'basic') {
        // Basic-auth apps use a modal flow instead of an OAuth popup.
        setBasicAuthApp(app);
        return;
      }
      if (authType === 'api_key') {
        // API-key apps collect the key in a dedicated modal — no OAuth popup.
        setApiKeyApp(app);
        return;
      }
      setConnectingApp(app);
      setIsConnecting(true);
      connectingRef.current = false;
      oauthPopupRef.current = openOAuthWindow(app.short_name, 'default');
    },
    [isConnecting, trackEvent, startConnectCard],
  );

  // Recover from a cancelled OAuth: the flow completes only via a postMessage
  // from the popup, so if the user closes it without finishing, `isConnecting`
  // would otherwise stay true forever (spinner stuck, and `handleConnectApp`
  // blocks any further connect). Poll the popup handle and, once it closes
  // without a token landing, clear the connecting state. The delayed re-check
  // guards the success race — on success the popup also closes, but a token
  // (or an in-flight creation) is present, so we skip the reset.
  useEffect(() => {
    if (!isConnecting) return;
    const popup = oauthPopupRef.current;
    if (!popup) return;

    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    const interval = setInterval(() => {
      if (!popup.closed) return;
      clearInterval(interval);
      settleTimer = setTimeout(() => {
        if (connectingRef.current) return;
        if (useAppStore.getState().authDetails.accessToken) return;
        setIsConnecting(false);
        setConnectingApp(null);
        oauthPopupRef.current = null;
      }, 500);
    }, 500);

    return () => {
      clearInterval(interval);
      if (settleTimer) clearTimeout(settleTimer);
    };
  }, [isConnecting]);

  const closeBasicAuthModal = useCallback(() => {
    setBasicAuthApp(null);
  }, []);

  const handleBasicAuthSuccess = useCallback(() => {
    if (basicAuthApp) {
      trackEvent(ANALYTICS_EVENTS.INTEGRATION_CONNECTED, {
        provider: basicAuthApp.short_name,
        auth_type: 'basic',
      });
    }
    setBasicAuthApp(null);
    onConnectionChange?.();
  }, [onConnectionChange, basicAuthApp, trackEvent]);

  const closeApiKeyModal = useCallback(() => {
    setApiKeyApp(null);
  }, []);

  const handleApiKeySuccess = useCallback(() => {
    if (apiKeyApp) {
      trackEvent(ANALYTICS_EVENTS.INTEGRATION_CONNECTED, {
        provider: apiKeyApp.short_name,
        auth_type: 'api_key',
      });
    }
    setApiKeyApp(null);
    onConnectionChange?.();
  }, [onConnectionChange, apiKeyApp, trackEvent]);

  const connectAppByShortName = useCallback(
    (shortName: string) => {
      const app = apps.find((a) => a.short_name === shortName);
      if (app) handleConnectApp(app);
    },
    [apps, handleConnectApp],
  );

  // Start the warehouse-sync OAuth for a parent app's hidden dataset sub-app
  // (e.g. google_ads → google_ads_dataset). The sub-app isn't in the public
  // catalog, so we synthesize the minimal shape handleConnectApp needs; the
  // server OAuth route resolves it via the full app registry.
  const connectDatasetSync = useCallback(
    (parentShortName: string) => {
      const sub = DATASET_SYNC_SUBAPPS[parentShortName];
      if (!sub || isConnecting) return;

      // Connect Card apps have no OAuth of ours to run. The server creates the
      // dataset and a paused Fivetran connection, then hands back a hosted URL
      // the user authorizes on. Fivetran's webhook finishes the setup, so there
      // is no token for the popup to send back — it just closes itself.
      if (sub.connect_card) {
        startConnectCard(sub.short_name, sub.display_name);
        return;
      }

      handleConnectApp({
        short_name: sub.short_name,
        display_name: sub.display_name,
        auth: [{ type: 'oauth' }],
      } as unknown as App);
    },
    [isConnecting, handleConnectApp, startConnectCard],
  );

  // Handle OAuth completion for new connections
  useEffect(() => {
    if (
      isConnecting &&
      connectingApp &&
      accessToken &&
      !connectingRef.current &&
      !createConnectionMutation.isPending
    ) {
      connectingRef.current = true;

      const createConnection = async () => {
        try {
          const oauthPayload: OauthPayload = {
            app_name: connectingApp.short_name,
            display_name: connectingApp.display_name,
            auth_name: 'default',
            token_type: 'oauth',
            access_token: accessToken as string,
            refresh_token: refreshToken as string,
            principal_id: userId as string,
            principal_name: userName as string,
            principal_email: userEmail as string,
          };

          const result = await createConnectionMutation.mutateAsync({
            oauthPayload,
            appName: connectingApp.short_name,
          });

          setAuthDetails({
            userId: '',
            userName: '',
            userEmail: '',
            accessToken: '',
            refreshToken: '',
          });

          const credentialId = result?.credential?.id;

          if (APPS_REQUIRING_CONFIG.includes(connectingApp.short_name)) {
            if (!credentialId) {
              toast({
                title: 'Error',
                description: 'Failed to retrieve credential. Please try reconnecting.',
                variant: 'destructive',
              });
              onConnectionChange?.();
              return;
            }

            if (APPS_WITH_AUTO_DATASET.includes(connectingApp.short_name)) {
              try {
                await createDatasetMutation.mutateAsync({
                  credentialId,
                  displayName: connectingApp.display_name,
                });
              } catch (datasetErr) {
                logger(datasetErr as Error, { context: 'Error creating dataset on connection' });
              }
            }

            setPostConnectionConfig({
              credentialId,
              appName: connectingApp.short_name,
              displayName: connectingApp.display_name,
            });
            toast({
              title: 'Connected',
              description: `${connectingApp.display_name} connected. Please complete the configuration.`,
            });
          } else {
            toast({
              title: 'Success',
              description: `${connectingApp.display_name} connected successfully`,
            });
          }

          trackEvent(ANALYTICS_EVENTS.INTEGRATION_CONNECTED, {
            provider: connectingApp.short_name,
            auth_type: 'oauth',
          });

          onConnectionChange?.();
        } catch (err) {
          logger(err as Error, { context: 'Error creating connection' });
          const isConflict = err instanceof ApiError && err.status === 409;
          trackEvent(ANALYTICS_EVENTS.INTEGRATION_CONNECT_FAILED, {
            provider: connectingApp.short_name,
            auth_type: 'oauth',
            error_code: err instanceof ApiError ? String(err.status) : 'unknown',
          });
          toast({
            title: isConflict ? 'Already connected' : 'Error',
            description: isConflict
              ? err.message
              : 'Failed to create connection. Please try again.',
            variant: 'destructive',
          });
        } finally {
          setIsConnecting(false);
          setConnectingApp(null);
          connectingRef.current = false;
        }
      };

      createConnection();
    }
  }, [
    accessToken,
    refreshToken,
    userId,
    userName,
    userEmail,
    isConnecting,
    connectingApp,
    createConnectionMutation,
    createDatasetMutation,
    onConnectionChange,
    setAuthDetails,
    trackEvent,
  ]);

  const handleConfigComplete = useCallback(() => {
    setPostConnectionConfig(null);
  }, []);

  const handleConfigure = useCallback(
    (connectionId: string, connectionType: string, displayName: string) => {
      setPostConnectionConfig({
        credentialId: connectionId,
        appName: connectionType,
        displayName,
      });
    },
    [],
  );

  return {
    connectingApp,
    isConnecting,
    postConnectionConfig,
    handleConnectApp,
    connectAppByShortName,
    connectDatasetSync,
    hasDatasetSyncSubApp: (shortName: string) => Boolean(DATASET_SYNC_SUBAPPS[shortName]),
    handleConfigComplete,
    handleConfigure,
    appsRequiringConfig: APPS_REQUIRING_CONFIG,
    appsWithAutoDataset: APPS_WITH_AUTO_DATASET,
    basicAuthApp,
    closeBasicAuthModal,
    handleBasicAuthSuccess,
    apiKeyApp,
    closeApiKeyModal,
    handleApiKeySuccess,
  };
}
